import { css, styled } from 'next-yak';

import { createPolymorphicElement } from '@xing-com/polymorphic-element';
import { scale050, xdlColorText } from '@xing-com/tokens';

import type { CaptionFontWeight, CaptionLineHeight } from './caption.types';

type CaptionTProps = {
  $ellipsis?: boolean;
  $fontWeight?: CaptionFontWeight;
  $lineHeight?: CaptionLineHeight;
  $noMargin?: boolean;
};

// *** Base ***
const baseCaption = css`
  color: ${xdlColorText};
  font-size: ${scale050};
  line-height: 1.5;
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  padding: 0;
`;

const noMargin = css`
  && {
    margin: 0;
  }
`;

const ellipsis = css`
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// *** Line-Heights ***
export const lineHeights = {
  dense: css`
    line-height: 1.25;
  `,
  increased: css`
    line-height: 1.65;
  `,
};

// *** Font-Weights ***
export const fontWeights = {
  regular: css`
    font-weight: 400;
  `,
  bold: css`
    font-weight: 700;
  `,
};

// *** Components ***
const CaptionElement = createPolymorphicElement('p', ['textalign']);

const Caption = styled(CaptionElement)<CaptionTProps>`
  ${baseCaption};

  ${({ $lineHeight }) => {
    switch ($lineHeight) {
      case 'dense':
        return lineHeights.dense;
      case 'increased':
        return lineHeights.increased;
      default:
        return $lineHeight satisfies undefined;
    }
  }};

  ${({ $fontWeight }) => {
    switch ($fontWeight) {
      case 'bold':
        return fontWeights.bold;
      case 'regular':
        return fontWeights.regular;
      default:
        return $fontWeight satisfies undefined;
    }
  }};

  ${({ $noMargin }) => $noMargin && noMargin};
  ${({ $ellipsis }) => $ellipsis && ellipsis};
`;

export const Styled = {
  Caption,
};
